import { z } from "zod";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

const uuid = z.uuid().toLowerCase();
const identity = {
  version: z.literal(2),
  parentPublicId: z.uuidv4().toLowerCase(),
  key: uuid,
  uploadKey: uuid,
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
};
const uploaded = { ...identity, uploadId: uuid };
const accepting = {
  ...uploaded,
  baseVersion: z.string().regex(/^[a-f0-9]{64}$/),
};
const schema = z.discriminatedUnion("kind", [
  z.strictObject({ ...identity, kind: z.literal("intent") }),
  z.strictObject({ ...uploaded, kind: z.literal("upload") }),
  z.strictObject({ ...accepting, kind: z.literal("accepting") }),
  z.strictObject({ ...accepting, kind: z.literal("accepted") }),
  z.strictObject({ ...accepting, kind: z.literal("rejected") }),
]);
export type VisitAudioAttachment = z.output<typeof schema>;
type Intent = Extract<VisitAudioAttachment, { kind: "intent" }>;
type Uploaded = Extract<VisitAudioAttachment, { kind: "upload" }>;
type Accepting = Extract<VisitAudioAttachment, { kind: "accepting" }>;
const storageKey = (account: string, parentPublicId: string) =>
  `rosc:visit-audio-attachment:${encodeURIComponent(account)}:${parentPublicId}`;
export const readVisitAudioAttachment = (
  account: string,
  parentPublicId: string,
): VisitAudioAttachment | null => {
  assertNoObsoleteSubmission(account, "rosc:visit-audio-attachment:");
  try {
    const value = sessionStorage.getItem(storageKey(account, parentPublicId));
    if (value === null) return null;
    const state = schema.parse(JSON.parse(value));
    if (state.parentPublicId !== parentPublicId) throw Error("Parent changed");
    return state;
  } catch {
    throw Error(
      "Saved audio attachment cannot be read. Check Visits before retrying.",
    );
  }
};
const save = (account: string, state: VisitAudioAttachment): void => {
  const parsed = schema.parse(state);
  try {
    sessionStorage.setItem(
      storageKey(account, state.parentPublicId),
      JSON.stringify(parsed),
    );
  } catch {
    throw Error(
      "Audio attachment identity could not be saved. Check Visits before retrying.",
    );
  }
};
const transition = (
  account: string,
  expected: VisitAudioAttachment,
  next: VisitAudioAttachment,
): void => {
  if (
    JSON.stringify(
      readVisitAudioAttachment(account, expected.parentPublicId),
    ) !== JSON.stringify(schema.parse(expected))
  )
    throw Error("Audio attachment changed. Keep the original retry identity.");
  save(account, next);
};
export const prepareVisitAudioAttachment = (
  account: string,
  parentPublicId: string,
  fingerprint: string,
): VisitAudioAttachment => {
  const existing = readVisitAudioAttachment(account, parentPublicId);
  if (existing) {
    if (existing.fingerprint !== fingerprint)
      throw Error(
        "Audio attachment has changed. Retry the same file or check Visits.",
      );
    return existing;
  }
  const pending: Intent = {
    parentPublicId,
    fingerprint,
    key: crypto.randomUUID(),
    uploadKey: crypto.randomUUID(),
    version: 2,
    kind: "intent",
  };
  save(account, pending);
  return pending;
};
export const rememberVisitAudioUpload = (
  account: string,
  expected: Intent,
  uploadId: string,
): Uploaded => {
  const next: Uploaded = {
    ...expected,
    uploadId: uuid.parse(uploadId),
    version: 2,
    kind: "upload",
  };
  transition(account, expected, next);
  return next;
};
export const beginVisitAudioAttachment = (
  account: string,
  expected: Uploaded,
  baseVersion: string,
): Accepting => {
  const next: Accepting = {
    ...expected,
    baseVersion,
    version: 2,
    kind: "accepting",
  };
  transition(account, expected, next);
  return next;
};
export const acceptVisitAudioAttachment = (
  account: string,
  expected: Accepting,
) => {
  const next: Extract<VisitAudioAttachment, { kind: "accepted" }> = {
    ...expected,
    version: 2,
    kind: "accepted",
  };
  transition(account, expected, next);
  return next;
};
export const rejectVisitAudioAttachment = (
  account: string,
  expected: Accepting,
): void => {
  transition(account, expected, { ...expected, version: 2, kind: "rejected" });
};
export const abandonVisitAudioAttachment = (
  account: string,
  parentPublicId: string,
): void => {
  discardObsoleteSubmissions(account, "rosc:visit-audio-attachment:");
  if (readVisitAudioAttachment(account, parentPublicId)?.kind === "accepting")
    throw Error(
      "Audio attachment may have succeeded. Reselect the target in Visits before retrying. Your local draft is retained.",
    );
  try {
    sessionStorage.removeItem(storageKey(account, parentPublicId));
  } catch {
    throw Error("Audio attachment identity could not be removed.");
  }
};
